<template><div><dt-notice kind="warning" title="Use DtText over CSS Utilities" :show-close="false" class="d-wmx100p d-my-200 dialtone-doc-notice">
Reach for the <a href="/components/text" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">DtText</a> component before considering any typography utility.
</dt-notice>
<h2 id="use-dttext-s-tone-prop" tabindex="-1" class="d-docsite--header-2">Use DtText's <code v-pre>tone</code> prop <a class="header-anchor d-link d-docsite--link d-link" href="#use-dttext-s-tone-prop" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use <a href="/components/text.html#tone" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">DtText's</a> <code v-pre>tone</code> prop to declare the text's tone, which will map to a foreground color. By default, the tone is inherited from its parent.</p>
<code-example source-code='&lt;dt-text tone=&quot;primary&quot;&gt;primary&lt;/dt-text&gt;
&lt;dt-text tone=&quot;secondary&quot;&gt;secondary&lt;/dt-text&gt;
&lt;dt-text tone=&quot;tertiary&quot;&gt;tertiary&lt;/dt-text&gt;
&lt;dt-text tone=&quot;muted&quot;&gt;muted&lt;/dt-text&gt;
&lt;dt-text tone=&quot;disabled&quot;&gt;disabled&lt;/dt-text&gt;
&lt;dt-text tone=&quot;placeholder&quot;&gt;placeholder&lt;/dt-text&gt;
&lt;dt-text tone=&quot;positive&quot;&gt;positive&lt;/dt-text&gt;
&lt;dt-text tone=&quot;positive-strong&quot;&gt;positive-strong&lt;/dt-text&gt;
&lt;dt-text tone=&quot;warning&quot;&gt;warning&lt;/dt-text&gt;
&lt;dt-text tone=&quot;critical&quot;&gt;critical&lt;/dt-text&gt;
&lt;dt-text tone=&quot;critical-strong&quot;&gt;critical-strong&lt;/dt-text&gt;'>
<dt-stack data-demo-wrapper class="d-py-100 d-px-200 d-bgc-primary d-bar-300">
  <dt-text tone="primary">primary</dt-text>
  <dt-text tone="secondary">secondary</dt-text>
  <dt-text tone="tertiary">tertiary</dt-text>
  <dt-text tone="muted">muted</dt-text>
  <dt-text tone="disabled">disabled</dt-text>
  <dt-text tone="placeholder">placeholder</dt-text>
  <dt-text tone="positive">positive</dt-text>
  <dt-text tone="positive-strong">positive-strong</dt-text>
  <dt-text tone="warning">warning</dt-text>
  <dt-text tone="critical">critical</dt-text>
  <dt-text tone="critical-strong">critical-strong</dt-text>
</dt-stack>
</code-example>
<code-example-tabs
vueCode='
<dt-text tone="{{tone}}">...</dt-text>
'/>
<h2 id="usage" tabindex="-1" class="d-docsite--header-2">Usage <a class="header-anchor d-link d-docsite--link d-link" href="#usage" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use <code v-pre>d-fc-{color}</code> to change an element's text color.</p>
<code-example source-code='&lt;p class=&quot;d-fc-critical&quot;&gt;The quick brown fox jumps over the lazy dog.&lt;/p&gt;'>
<p class="d-fc-critical">The quick brown fox jumps over the lazy dog.</p>
</code-example>
<h2 id="hover" tabindex="-1" class="d-docsite--header-2">Hover <a class="header-anchor d-link d-docsite--link d-link" href="#hover" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use <code v-pre>h:d-fc-{color}</code> to change an element's text color <code v-pre>:hover</code> state.</p>
<code-example source-code='&lt;dt-button kind=&quot;unstyled&quot; class=&quot;d-fc-critical h:d-fc-positive&quot;&gt;Hover over me&lt;/dt-button&gt;'>
<dt-button kind="unstyled" class="d-fc-critical h:d-fc-positive">Hover over me</dt-button>
</code-example>
<h2 id="focus" tabindex="-1" class="d-docsite--header-2">Focus <a class="header-anchor d-link d-docsite--link d-link" href="#focus" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use <code v-pre>f:d-fc-{color}</code> to change an element's text color <code v-pre>:focus</code> and <code v-pre>:focus-within</code> state.</p>
<code-example source-code='&lt;dt-button kind=&quot;unstyled&quot; class=&quot;d-fc-critical f:d-fc-positive&quot;&gt;Focus me&lt;/dt-button&gt;'>
<dt-button kind="unstyled" class="d-fc-critical f:d-fc-positive">Focus me</dt-button>
</code-example>
<h2 id="focus-visible" tabindex="-1" class="d-docsite--header-2">Focus Visible <a class="header-anchor d-link d-docsite--link d-link" href="#focus-visible" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use <code v-pre>fv:d-fc-{color}</code> to change an element's text color on <code v-pre>:focus-visible</code> state [only when focused by keyboard].</p>
<code-example source-code='&lt;dt-button kind=&quot;unstyled&quot; class=&quot;d-fc-critical fv:d-fc-positive&quot;&gt;Keyboard focus me&lt;/dt-button&gt;'>
<dt-button kind="unstyled" class="d-fc-critical fv:d-fc-positive">Keyboard focus me</dt-button>
</code-example>
<h2 id="inverted" tabindex="-1" class="d-docsite--header-2">Inverted <a class="header-anchor d-link d-docsite--link d-link" href="#inverted" aria-hidden="true">#</a></h2>
<dt-notice kind="info" title="Tip" :show-close="false" class="d-wmx100p d-my-200 dialtone-doc-notice">
Avoid <code v-pre>-inverted</code> utility variants, which will be sunset. Use the <a href="/components/mode-island.html#inverting" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">v-dt-mode directive</a> with base classes instead -- it automatically resolves the correct colors for the current mode.
</dt-notice>
<code-example source-code='&lt;dt-stack direction=&quot;row&quot; gap=&quot;400&quot;&gt;
  &lt;dt-stack class=&quot;d-py-100 d-px-200 d-bgc-primary d-bar-300&quot;&gt;
    &lt;dt-text tone=&quot;primary&quot;&gt;primary&lt;/dt-text&gt;
    &lt;dt-text tone=&quot;secondary&quot;&gt;secondary&lt;/dt-text&gt;
    &lt;dt-text tone=&quot;tertiary&quot;&gt;tertiary&lt;/dt-text&gt;
    &lt;dt-text tone=&quot;muted&quot;&gt;muted&lt;/dt-text&gt;
    &lt;dt-text tone=&quot;disabled&quot;&gt;disabled&lt;/dt-text&gt;
    &lt;dt-text tone=&quot;placeholder&quot;&gt;placeholder&lt;/dt-text&gt;
    &lt;dt-text tone=&quot;positive&quot;&gt;positive&lt;/dt-text&gt;
    &lt;dt-text tone=&quot;positive-strong&quot;&gt;positive-strong&lt;/dt-text&gt;
    &lt;dt-text tone=&quot;warning&quot;&gt;warning&lt;/dt-text&gt;
    &lt;dt-text tone=&quot;critical&quot;&gt;critical&lt;/dt-text&gt;
    &lt;dt-text tone=&quot;critical-strong&quot;&gt;critical-strong&lt;/dt-text&gt;
  &lt;/dt-stack&gt;
  &lt;dt-stack class=&quot;d-py-100 d-px-200 d-bgc-contrast d-bar-300&quot;&gt;
    &lt;dt-text v-dt-mode:invert tone=&quot;primary&quot;&gt;primary&lt;/dt-text&gt;
    &lt;dt-text v-dt-mode:invert tone=&quot;secondary&quot;&gt;secondary&lt;/dt-text&gt;
    &lt;dt-text v-dt-mode:invert tone=&quot;tertiary&quot;&gt;tertiary&lt;/dt-text&gt;
    &lt;dt-text v-dt-mode:invert tone=&quot;muted&quot;&gt;muted&lt;/dt-text&gt;
    &lt;dt-text v-dt-mode:invert tone=&quot;disabled&quot;&gt;disabled&lt;/dt-text&gt;
    &lt;dt-text v-dt-mode:invert tone=&quot;placeholder&quot;&gt;placeholder&lt;/dt-text&gt;
    &lt;dt-text v-dt-mode:invert tone=&quot;positive&quot;&gt;positive&lt;/dt-text&gt;
    &lt;dt-text v-dt-mode:invert tone=&quot;positive-strong&quot;&gt;positive-strong&lt;/dt-text&gt;
    &lt;dt-text v-dt-mode:invert tone=&quot;warning&quot;&gt;warning&lt;/dt-text&gt;
    &lt;dt-text v-dt-mode:invert tone=&quot;critical&quot;&gt;critical&lt;/dt-text&gt;
    &lt;dt-text v-dt-mode:invert tone=&quot;critical-strong&quot;&gt;critical-strong&lt;/dt-text&gt;
  &lt;/dt-stack&gt;
&lt;/dt-stack&gt;'>
<dt-stack direction="row" gap="400">
  <dt-stack class="d-py-100 d-px-200 d-bgc-primary d-bar-300">
    <dt-text tone="primary">primary</dt-text>
    <dt-text tone="secondary">secondary</dt-text>
    <dt-text tone="tertiary">tertiary</dt-text>
    <dt-text tone="muted">muted</dt-text>
    <dt-text tone="disabled">disabled</dt-text>
    <dt-text tone="placeholder">placeholder</dt-text>
    <dt-text tone="positive">positive</dt-text>
    <dt-text tone="positive-strong">positive-strong</dt-text>
    <dt-text tone="warning">warning</dt-text>
    <dt-text tone="critical">critical</dt-text>
    <dt-text tone="critical-strong">critical-strong</dt-text>
  </dt-stack>
  <dt-stack class="d-py-100 d-px-200 d-bgc-contrast d-bar-300">
    <dt-text v-dt-mode:invert tone="primary">primary</dt-text>
    <dt-text v-dt-mode:invert tone="secondary">secondary</dt-text>
    <dt-text v-dt-mode:invert tone="tertiary">tertiary</dt-text>
    <dt-text v-dt-mode:invert tone="muted">muted</dt-text>
    <dt-text v-dt-mode:invert tone="disabled">disabled</dt-text>
    <dt-text v-dt-mode:invert tone="placeholder">placeholder</dt-text>
    <dt-text v-dt-mode:invert tone="positive">positive</dt-text>
    <dt-text v-dt-mode:invert tone="positive-strong">positive-strong</dt-text>
    <dt-text v-dt-mode:invert tone="warning">warning</dt-text>
    <dt-text v-dt-mode:invert tone="critical">critical</dt-text>
    <dt-text v-dt-mode:invert tone="critical-strong">critical-strong</dt-text>
  </dt-stack>
</dt-stack>
</code-example>
<code-example-tabs
vueCode='
<dt-stack gap="300" class="d-py-100 d-px-200 d-bgc-contrast d-bar-300">
  <dt-text v-dt-mode:invert tone="primary">primary</dt-text>
  <dt-text v-dt-mode:invert tone="secondary">secondary</dt-text>
  <dt-text v-dt-mode:invert tone="tertiary">tertiary</dt-text>
  <dt-text v-dt-mode:invert tone="muted">muted</dt-text>
  <dt-text v-dt-mode:invert tone="disabled">disabled</dt-text>
  <dt-text v-dt-mode:invert tone="placeholder">placeholder</dt-text>
  <dt-text v-dt-mode:invert tone="positive">positive</dt-text>
  <dt-text v-dt-mode:invert tone="positive-strong">positive-strong</dt-text>
  <dt-text v-dt-mode:invert tone="warning">warning</dt-text>
  <dt-text v-dt-mode:invert tone="critical">critical</dt-text>
  <dt-text v-dt-mode:invert tone="critical-strong">critical-strong</dt-text>
</dt-stack>
'/>
<h2 id="changing-opacity" tabindex="-1" class="d-docsite--header-2">Changing Opacity <a class="header-anchor d-link d-docsite--link d-link" href="#changing-opacity" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use <code v-pre>d-fco{n}</code> to change an element's text color opacity. You can also change font color opacity on <code v-pre>:hover</code>, <code v-pre>:focus</code>,
<code v-pre>:focus-visible</code> by using the respective <code v-pre>h:d-fco{n}</code>, <code v-pre>f:d-fco{n}</code>, <code v-pre>fv:d-fco{n}</code> prefixes.</p>
<code-example source-code='&lt;p class=&quot;d-fc-critical&quot;&gt;The quick brown fox jumps over the lazy dog.&lt;/p&gt;
&lt;p class=&quot;d-fc-critical d-fco99&quot;&gt;The quick brown fox jumps over the lazy dog.&lt;/p&gt;
&lt;p class=&quot;d-fc-critical d-fco95&quot;&gt;The quick brown fox jumps over the lazy dog.&lt;/p&gt;
&lt;p class=&quot;d-fc-critical d-fco90&quot;&gt;The quick brown fox jumps over the lazy dog.&lt;/p&gt;
&lt;p class=&quot;d-fc-critical d-fco75&quot;&gt;The quick brown fox jumps over the lazy dog.&lt;/p&gt;
&lt;p class=&quot;d-fc-critical d-fco50&quot;&gt;The quick brown fox jumps over the lazy dog.&lt;/p&gt;
&lt;p class=&quot;d-fc-critical d-fco25&quot;&gt;The quick brown fox jumps over the lazy dog.&lt;/p&gt;
&lt;p class=&quot;d-fc-critical d-fco10&quot;&gt;The quick brown fox jumps over the lazy dog.&lt;/p&gt;
&lt;p class=&quot;d-fc-critical d-fco0&quot;&gt;The quick brown fox jumps over the lazy dog.&lt;/p&gt;'>
<p class="d-fc-critical">The quick brown fox jumps over the lazy dog.</p>
<p class="d-fc-critical d-fco99">The quick brown fox jumps over the lazy dog.</p>
<p class="d-fc-critical d-fco95">The quick brown fox jumps over the lazy dog.</p>
<p class="d-fc-critical d-fco90">The quick brown fox jumps over the lazy dog.</p>
<p class="d-fc-critical d-fco75">The quick brown fox jumps over the lazy dog.</p>
<p class="d-fc-critical d-fco50">The quick brown fox jumps over the lazy dog.</p>
<p class="d-fc-critical d-fco25">The quick brown fox jumps over the lazy dog.</p>
<p class="d-fc-critical d-fco10">The quick brown fox jumps over the lazy dog.</p>
<p class="d-fc-critical d-fco0">The quick brown fox jumps over the lazy dog.</p>
</code-example>
<h2 id="classes" tabindex="-1" class="d-docsite--header-2">Classes <a class="header-anchor d-link d-docsite--link d-link" href="#classes" aria-hidden="true">#</a></h2>
<new-utility-class-table :classes="fontColors">
  <template #example="{ className }">
    <dt-stack direction="row" align="center" justify="center" class="d-fl-shrink0 d-h42 d-w42 d-bar-circle d-ba d-bc-moderate" :class="[
        {'d-bgc-primary': !className.endsWith('inverted')},
        {'d-bgc-contrast': className.endsWith('inverted')},
        className
      ]">
      <span>Aa</span>
    </dt-stack>
  </template>
</new-utility-class-table>
</div></template>


<script setup>
  import { inject } from 'vue';
  import { extractUtilityClasses } from '@utilities';

  const utilityClassDocs = inject('utilityClassDocs');
  const fontColors = extractUtilityClasses(utilityClassDocs, 'd-fc-');
</script>